import React, { useState, useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceDot
} from 'recharts';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import { Badge } from './ui/badge';
import {
  WHO_WEIGHT_FOR_AGE_BOYS,
  WHO_WEIGHT_FOR_AGE_GIRLS,
  WHO_HEIGHT_FOR_AGE_BOYS,
  WHO_HEIGHT_FOR_AGE_GIRLS,
  WHO_HEAD_CIRC_FOR_AGE_BOYS,
  WHO_HEAD_CIRC_FOR_AGE_GIRLS,
  WHO_WEIGHT_FOR_HEIGHT_BOYS,
  WHO_WEIGHT_FOR_HEIGHT_GIRLS,
  WHO_WEIGHT_FOR_AGE_5_18_BOYS,
  WHO_WEIGHT_FOR_AGE_5_18_GIRLS,
  WHO_HEIGHT_FOR_AGE_5_18_BOYS,
  WHO_HEIGHT_FOR_AGE_5_18_GIRLS,
  WHO_BMI_FOR_AGE_BOYS,
  WHO_BMI_FOR_AGE_GIRLS,
  generateChartData,
  generateWeightForHeightData,
  generateChartData0to18,
  calculatePercentile,
  getGrowthStatus
} from '../utils/whoGrowthData';

// Clean color scheme - blue percentiles with black patient data
const COLORS = {
  percentile: '#3b82f6',     // Blue for percentile lines
  patient: '#1f2937',        // Dark gray/black for patient data
  grid: '#eff6ff',           // Very light blue for grid
  gridMajor: '#dbeafe',      // Slightly darker blue for major grid lines
  background: '#f8fafc',     // Very light blue-gray background
  text: '#374151',           // Dark gray text
  success: '#22c55e',
  warning: '#f97316',
  danger: '#dc2626'
};

// Calculate age in months from birth date
const calculateAgeInMonths = (birthDate, measurementDate) => {
  const parseDateString = (dateStr) => {
    if (!dateStr) return null;
    
    // Handle Date objects
    if (dateStr instanceof Date) return dateStr;
    
    // Handle string dates
    if (typeof dateStr === 'string') {
      // Handle DD/MM/YYYY format
      if (dateStr.includes('/')) {
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          const [day, month, year] = parts.map(Number);
          return new Date(year, month - 1, day);
        }
      }
      // Handle YYYY-MM-DD format
      if (dateStr.includes('-')) {
        const parts = dateStr.split('-');
        if (parts.length === 3) {
          // Check if it's YYYY-MM-DD or DD-MM-YYYY
          if (parts[0].length === 4) {
            return new Date(dateStr);
          } else {
            const [day, month, year] = parts.map(Number);
            return new Date(year, month - 1, day);
          }
        }
      }
      // Try direct parsing
      return new Date(dateStr);
    }
    
    return null;
  };

  const birth = parseDateString(birthDate);
  const measurement = parseDateString(measurementDate);

  if (!birth || !measurement || isNaN(birth.getTime()) || isNaN(measurement.getTime())) {
    return null;
  }

  const years = measurement.getFullYear() - birth.getFullYear();
  const months = measurement.getMonth() - birth.getMonth();
  const days = measurement.getDate() - birth.getDate();

  let totalMonths = years * 12 + months;
  if (days < 0) totalMonths -= 1;

  return Math.max(0, totalMonths);
};

// Calculate BMI
const calculateBMI = (weight, height) => {
  if (!weight || !height || height === 0) return null;
  const heightInMeters = height / 100;
  return weight / (heightInMeters * heightInMeters);
};

// Custom tooltip component
const CustomTooltip = ({ active, payload, label, chartType, yUnit }) => {
  if (!active || !payload || !payload.length) return null;

  const formatLabel = () => {
    if (chartType === 'weightForHeight') return `Height: ${label} cm`;
    if (chartType === 'age18') {
      const years = Math.floor(label / 12);
      const months = label % 12;
      return months > 0 ? `Age: ${years}y ${months}mo` : `Age: ${years} years`;
    }
    return `Age: ${label} months`;
  };

  return (
    <div className="bg-white px-4 py-3 rounded-xl shadow-xl border border-blue-200 text-sm">
      <p className="font-semibold text-gray-800 mb-2 border-b border-blue-100 pb-2">{formatLabel()}</p>
      <div className="space-y-1">
        {payload.filter(p => p.value !== null && p.value !== undefined).map((entry, index) => (
          <div key={index} className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div 
                className="w-2 h-2 rounded-full" 
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-gray-600 text-xs">{entry.name}</span>
            </div>
            <span className="font-semibold text-gray-800">{entry.value?.toFixed(1)} {yUnit}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Get BMI status for teens (5-20 years) - uses percentile-based assessment
const getTeenBMIStatus = (bmiPercentile) => {
  if (!bmiPercentile) return null;
  
  // CDC/WHO BMI-for-age percentile categories for children and teens
  if (bmiPercentile.status === 'severe') {
    return { type: 'underweight', severity: 'severe', label: 'Underweight', color: '#dc2626', description: 'BMI below 3rd percentile for age' };
  } else if (bmiPercentile.status === 'low') {
    return { type: 'underweight', severity: 'moderate', label: 'Underweight', color: '#f97316', description: 'BMI between 3rd-15th percentile for age' };
  } else if (bmiPercentile.status === 'normal') {
    return { type: 'normal', severity: 'normal', label: 'Healthy Weight', color: '#22c55e', description: 'BMI between 15th-85th percentile for age' };
  } else if (bmiPercentile.status === 'high') {
    return { type: 'overweight', severity: 'moderate', label: 'Overweight', color: '#f97316', description: 'BMI between 85th-97th percentile for age' };
  } else if (bmiPercentile.status === 'very_high') {
    return { type: 'overweight', severity: 'severe', label: 'Obese', color: '#dc2626', description: 'BMI above 97th percentile for age' };
  }
  return null;
};

// Get BMI status for adults (>20 years) - considers age and gender
const getAdultBMIStatus = (bmi, ageMonths, sex) => {
  if (!bmi || isNaN(bmi)) return null;
  
  const ageYears = ageMonths ? Math.floor(ageMonths / 12) : 25;
  const isFemale = sex === 'female';
  const isElderly = ageYears >= 65;
  
  // Adjusted BMI categories based on age and gender
  // Elderly adults (65+) have slightly higher healthy BMI range
  // Gender-specific adjustments for optimal ranges
  
  let underweightSevere, underweightModerate, underweightMild, normalMax, overweightMax;
  
  if (isElderly) {
    // Elderly: slightly higher BMI is considered healthy
    underweightSevere = 17;
    underweightModerate = 18;
    underweightMild = 20;
    normalMax = 27;
    overweightMax = 32;
  } else if (isFemale) {
    // Adult females
    underweightSevere = 16;
    underweightModerate = 17;
    underweightMild = 18.5;
    normalMax = 24;
    overweightMax = 29;
  } else {
    // Adult males
    underweightSevere = 16;
    underweightModerate = 17;
    underweightMild = 18.5;
    normalMax = 25;
    overweightMax = 30;
  }
  
  // Determine status based on adjusted ranges
  if (bmi < underweightSevere) {
    return { 
      type: 'underweight', 
      severity: 'severe', 
      label: 'Severely Underweight', 
      color: '#dc2626', 
      description: `BMI < ${underweightSevere} indicates severe underweight${isElderly ? ' for elderly' : ''}` 
    };
  } else if (bmi < underweightModerate) {
    return { 
      type: 'underweight', 
      severity: 'moderate', 
      label: 'Moderately Underweight', 
      color: '#f97316', 
      description: `BMI ${underweightSevere}-${underweightModerate} indicates moderate underweight` 
    };
  } else if (bmi < underweightMild) {
    return { 
      type: 'underweight', 
      severity: 'mild', 
      label: 'Underweight', 
      color: '#eab308', 
      description: `BMI ${underweightModerate}-${underweightMild} indicates underweight` 
    };
  } else if (bmi < normalMax) {
    return { 
      type: 'normal', 
      severity: 'normal', 
      label: 'Normal Weight', 
      color: '#22c55e', 
      description: `BMI ${underweightMild}-${normalMax} is healthy weight${isFemale ? ' for females' : isElderly ? ' for elderly' : ' for males'}` 
    };
  } else if (bmi < overweightMax) {
    return { 
      type: 'overweight', 
      severity: 'moderate', 
      label: 'Overweight', 
      color: '#f97316', 
      description: `BMI ${normalMax}-${overweightMax} indicates overweight` 
    };
  } else if (bmi < 35) {
    return { 
      type: 'overweight', 
      severity: 'severe', 
      label: 'Obese (Class I)', 
      color: '#dc2626', 
      description: `BMI ${overweightMax}-35 indicates obesity` 
    };
  } else if (bmi < 40) {
    return { 
      type: 'overweight', 
      severity: 'severe', 
      label: 'Obese (Class II)', 
      color: '#dc2626', 
      description: 'BMI 35-40 indicates severe obesity' 
    };
  } else {
    return { 
      type: 'overweight', 
      severity: 'severe', 
      label: 'Obese (Class III)', 
      color: '#dc2626', 
      description: 'BMI ≥40 indicates very severe obesity' 
    };
  }
};

// Growth status alert component with detailed percentile info
const GrowthStatusAlert = ({ statuses, weightPercentile, heightPercentile, headCircPercentile, ageMonths, sex, bmiValue, bmiPercentile, isOverWHOAge }) => {
  // Format age display
  const formatAge = (months) => {
    if (!months && months !== 0) return '';
    if (months < 12) return `${months} month${months !== 1 ? 's' : ''}`;
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    if (remainingMonths === 0) return `${years} year${years !== 1 ? 's' : ''}`;
    return `${years}y ${remainingMonths}m`;
  };

  // Check if there's any abnormality in WHO assessment
  const hasWHOIssue = statuses && statuses.some(s => s.type !== 'normal');
  
  // Determine age category
  const isAdult = ageMonths !== null && ageMonths !== undefined && ageMonths > 240; // >20 years
  const isTeen = ageMonths !== null && ageMonths !== undefined && ageMonths > 60 && ageMonths <= 240; // 5-20 years
  
  // Show WHO Growth Assessment if:
  // 1. Child is within WHO chart age (0-5 years), OR
  // 2. Child is over 5 years BUT has abnormal WHO assessment (continue showing until normal)
  const showWHOAssessment = !isOverWHOAge || hasWHOIssue;
  
  if (showWHOAssessment) {
    if (!statuses || statuses.length === 0) return null;
    
    return (
      <div className={`rounded-xl p-4 mb-4 ${hasWHOIssue ? 'bg-orange-50 border border-orange-200' : 'bg-green-50 border border-green-200'}`}>
        <div className="flex items-start gap-3">
          {hasWHOIssue ? (
            <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
          ) : (
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <h4 className="font-semibold text-gray-800">Growth Assessment</h4>
              {(ageMonths !== undefined || sex) && (
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  {ageMonths !== undefined && <span>Age: {formatAge(ageMonths)}</span>}
                  {sex && <span>• {sex === 'female' ? 'Girl' : 'Boy'}</span>}
                </div>
              )}
            </div>
            
            <div className="flex flex-wrap gap-2 mb-2">
              {statuses.map((status, idx) => (
                <Badge 
                  key={idx}
                  variant="outline"
                  className="text-xs"
                  style={{ 
                    borderColor: status.color, 
                    color: status.color,
                    backgroundColor: `${status.color}10`
                  }}
                >
                  {status.label}
                </Badge>
              ))}
            </div>
            
            {/* Percentile details */}
            {(weightPercentile || heightPercentile || headCircPercentile) && (
              <div className="flex flex-wrap gap-3 text-xs mt-2 pt-2 border-t border-gray-200">
                {weightPercentile && (
                  <div className="flex items-center gap-1">
                    <span className="text-gray-500">Weight:</span>
                    <span className="font-medium" style={{ color: weightPercentile.color }}>
                      {weightPercentile.percentile}th percentile
                    </span>
                  </div>
                )}
                {heightPercentile && (
                  <div className="flex items-center gap-1">
                    <span className="text-gray-500">Height:</span>
                    <span className="font-medium" style={{ color: heightPercentile.color }}>
                      {heightPercentile.percentile}th percentile
                    </span>
                  </div>
                )}
                {headCircPercentile && (
                  <div className="flex items-center gap-1">
                    <span className="text-gray-500">Head:</span>
                    <span className="font-medium" style={{ color: headCircPercentile.color }}>
                      {headCircPercentile.percentile}th percentile
                    </span>
                  </div>
                )}
              </div>
            )}
            
            {hasWHOIssue && (
              <p className="text-xs text-gray-600 mt-2">
                {statuses.find(s => s.type !== 'normal')?.description}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  // For teens (5-20 years) with normal WHO assessment, show teen BMI assessment (percentile-based)
  if (isTeen) {
    const teenBMIStatus = getTeenBMIStatus(bmiPercentile);
    if (!teenBMIStatus) return null;
    
    const isNormalBMI = teenBMIStatus.type === 'normal';
    
    return (
      <div className={`rounded-xl p-4 mb-4 ${isNormalBMI ? 'bg-green-50 border border-green-200' : 'bg-orange-50 border border-orange-200'}`}>
        <div className="flex items-start gap-3">
          {isNormalBMI ? (
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <h4 className="font-semibold text-gray-800">BMI Assessment (Teen)</h4>
              {(ageMonths !== undefined || sex) && (
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  {ageMonths !== undefined && <span>Age: {formatAge(ageMonths)}</span>}
                  {sex && <span>• {sex === 'female' ? 'Girl' : 'Boy'}</span>}
                </div>
              )}
            </div>
            
            <div className="flex flex-wrap items-center gap-3">
              <Badge 
                variant="outline"
                className="text-xs"
                style={{ 
                  borderColor: teenBMIStatus.color, 
                  color: teenBMIStatus.color,
                  backgroundColor: `${teenBMIStatus.color}10`
                }}
              >
                {teenBMIStatus.label}
              </Badge>
              
              <div className="text-sm">
                <span className="text-gray-500">BMI: </span>
                <span className="font-semibold" style={{ color: teenBMIStatus.color }}>{bmiValue?.toFixed(1)} kg/m²</span>
              </div>
              
              {bmiPercentile && (
                <div className="text-xs text-gray-500">
                  ({bmiPercentile.percentile}th percentile)
                </div>
              )}
            </div>
            
            {!isNormalBMI && (
              <p className="text-xs text-gray-600 mt-2">
                {teenBMIStatus.description}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  // For adults (>20 years), show standard adult BMI assessment
  if (isAdult) {
    const adultBMIStatus = getAdultBMIStatus(bmiValue, ageMonths, sex);
    if (!adultBMIStatus) return null;
    
    const isNormalBMI = adultBMIStatus.type === 'normal';
    
    return (
      <div className={`rounded-xl p-4 mb-4 ${isNormalBMI ? 'bg-green-50 border border-green-200' : 'bg-orange-50 border border-orange-200'}`}>
        <div className="flex items-start gap-3">
          {isNormalBMI ? (
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <h4 className="font-semibold text-gray-800">BMI Assessment (Adult)</h4>
              {(ageMonths !== undefined || sex) && (
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  {ageMonths !== undefined && <span>Age: {formatAge(ageMonths)}</span>}
                  {sex && <span>• {sex === 'female' ? 'Female' : 'Male'}</span>}
                </div>
              )}
            </div>
            
            <div className="flex flex-wrap items-center gap-3">
              <Badge 
                variant="outline"
                className="text-xs"
                style={{ 
                  borderColor: adultBMIStatus.color, 
                  color: adultBMIStatus.color,
                  backgroundColor: `${adultBMIStatus.color}10`
                }}
              >
                {adultBMIStatus.label}
              </Badge>
              
              <div className="text-sm">
                <span className="text-gray-500">BMI: </span>
                <span className="font-semibold" style={{ color: adultBMIStatus.color }}>{bmiValue?.toFixed(1)} kg/m²</span>
              </div>
            </div>
            
            {!isNormalBMI && (
              <p className="text-xs text-gray-600 mt-2">
                {adultBMIStatus.description}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  return null;
};

// Single chart component with clean WHO-style design
const GrowthChart = ({ 
  title, 
  data, 
  patientData, 
  xKey, 
  yLabel, 
  xLabel,
  yUnit,
  chartType = 'age',
  formatXAxis
}) => {
  // Combine WHO data with patient points
  const combinedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    return data.map(point => {
      const patientPoint = patientData?.find(p => {
        if (chartType === 'weightForHeight') {
          return Math.abs(p.height - point[xKey]) < 3;
        }
        return Math.abs(p.ageMonths - point[xKey]) < 1;
      });
      
      return {
        ...point,
        patient: patientPoint?.value || null
      };
    });
  }, [data, patientData, xKey, chartType]);

  // Find latest patient point for highlighting
  const latestPatient = patientData && patientData.length > 0 
    ? patientData[patientData.length - 1] 
    : null;

  // Format X axis for 0-18 years charts
  const xAxisTickFormatter = (value) => {
    if (formatXAxis === 'years') {
      const years = Math.floor(value / 12);
      return years;
    }
    return value;
  };

  // Get Y-axis domain with padding
  const getYDomain = () => {
    if (!combinedData || combinedData.length === 0) return ['auto', 'auto'];
    const allValues = combinedData.flatMap(d => [d.p3, d.p97, d.patient].filter(v => v !== null && v !== undefined));
    const min = Math.floor(Math.min(...allValues) * 0.9);
    const max = Math.ceil(Math.max(...allValues) * 1.05);
    return [min > 0 ? min : 0, max];
  };

  return (
    <div className="bg-white rounded-2xl border border-blue-100 shadow-sm overflow-hidden">
      {/* Chart Header */}
      <div className="px-5 py-4 border-b border-blue-100 bg-gradient-to-r from-blue-50 to-white">
        <h4 className="font-semibold text-gray-800">{title}</h4>
        <p className="text-xs text-gray-500 mt-0.5">WHO Child Growth Standards - Percentiles</p>
      </div>
      
      {/* Chart Area */}
      <div className="p-4" style={{ backgroundColor: COLORS.background }}>
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={combinedData} margin={{ top: 20, right: 60, left: 10, bottom: 20 }}>
            {/* Grid */}
            <CartesianGrid 
              strokeDasharray="0" 
              stroke={COLORS.grid}
              strokeWidth={1}
            />
            
            {/* X Axis */}
            <XAxis 
              dataKey={xKey} 
              tick={{ fontSize: 11, fill: COLORS.text }}
              tickFormatter={formatXAxis === 'years' ? xAxisTickFormatter : undefined}
              axisLine={{ stroke: COLORS.gridMajor, strokeWidth: 1 }}
              tickLine={{ stroke: COLORS.gridMajor }}
              label={{ 
                value: xLabel, 
                position: 'insideBottom', 
                offset: -10, 
                fontSize: 12,
                fill: COLORS.text,
                fontWeight: 500
              }}
            />
            
            {/* Y Axis */}
            <YAxis 
              domain={getYDomain()}
              tick={{ fontSize: 11, fill: COLORS.text }}
              axisLine={{ stroke: COLORS.gridMajor, strokeWidth: 1 }}
              tickLine={{ stroke: COLORS.gridMajor }}
              label={{ 
                value: yLabel, 
                angle: -90, 
                position: 'insideLeft', 
                fontSize: 12,
                fill: COLORS.text,
                fontWeight: 500,
                dx: 10
              }}
            />
            
            {/* Tooltip */}
            <Tooltip content={<CustomTooltip chartType={chartType} yUnit={yUnit} />} />
            
            {/* Percentile Lines - All in pink with different dash patterns */}
            <Line 
              type="monotone" 
              dataKey="p97" 
              stroke={COLORS.percentile}
              strokeWidth={1.5}
              dot={false}
              name="97th"
            />
            <Line 
              type="monotone" 
              dataKey="p85" 
              stroke={COLORS.percentile}
              strokeWidth={1.5}
              strokeDasharray="8 4"
              dot={false}
              name="85th"
            />
            <Line 
              type="monotone" 
              dataKey="p50" 
              stroke={COLORS.percentile}
              strokeWidth={2}
              dot={false}
              name="50th"
            />
            <Line 
              type="monotone" 
              dataKey="p15" 
              stroke={COLORS.percentile}
              strokeWidth={1.5}
              strokeDasharray="8 4"
              dot={false}
              name="15th"
            />
            <Line 
              type="monotone" 
              dataKey="p3" 
              stroke={COLORS.percentile}
              strokeWidth={1.5}
              dot={false}
              name="3rd"
            />
            
            {/* Patient data - Black line with dots */}
            {patientData && patientData.length > 0 && (
              <Line
                type="monotone"
                dataKey="patient"
                stroke={COLORS.patient}
                strokeWidth={2.5}
                dot={(props) => {
                  const { cx, cy, payload } = props;
                  if (payload.patient === null || payload.patient === undefined) return null;
                  
                  // Check if this is the latest point
                  const isLatest = latestPatient && 
                    ((chartType === 'weightForHeight' && Math.abs(payload[xKey] - latestPatient.height) < 3) ||
                     (chartType !== 'weightForHeight' && Math.abs(payload[xKey] - latestPatient.ageMonths) < 1));
                  
                  if (isLatest) {
                    // Larger, highlighted marker for latest reading
                    return (
                      <g key={`dot-${payload[xKey]}`}>
                        {/* Outer glow */}
                        <circle cx={cx} cy={cy} r={12} fill="#3b82f6" fillOpacity={0.2} />
                        {/* Middle ring */}
                        <circle cx={cx} cy={cy} r={8} fill="#fff" stroke="#3b82f6" strokeWidth={2} />
                        {/* Inner dot */}
                        <circle cx={cx} cy={cy} r={5} fill="#1f2937" />
                      </g>
                    );
                  }
                  
                  // Regular dot for other points
                  return (
                    <circle 
                      key={`dot-${payload[xKey]}`}
                      cx={cx} 
                      cy={cy} 
                      r={4} 
                      fill={COLORS.patient} 
                    />
                  );
                }}
                activeDot={{ r: 6, fill: COLORS.patient, stroke: '#fff', strokeWidth: 2 }}
                name="Patient"
                connectNulls
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
      
      {/* Bottom Legend & Info */}
      <div className="px-5 py-3 border-t border-blue-100 bg-white">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-6 h-0.5" style={{ backgroundColor: COLORS.percentile }} />
              <span className="text-gray-600">Percentiles</span>
            </div>
            {patientData && patientData.length > 0 && (
              <>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.patient }} />
                  <span className="text-gray-600">Readings</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative w-5 h-5 flex items-center justify-center">
                    <div className="absolute w-5 h-5 rounded-full bg-blue-500 opacity-20" />
                    <div className="absolute w-3.5 h-3.5 rounded-full bg-white border-2 border-blue-500" />
                    <div className="absolute w-2 h-2 rounded-full bg-gray-800" />
                  </div>
                  <span className="text-gray-600">Latest</span>
                </div>
              </>
            )}
          </div>
          
          {/* Latest measurement indicator */}
          {latestPatient && (
            <div className="flex items-center gap-2 text-xs bg-gray-50 px-3 py-1.5 rounded-full">
              <span className="text-gray-500">Latest:</span>
              <span className="font-semibold text-gray-800">
                {latestPatient.value?.toFixed(1)} {yUnit}
              </span>
              {latestPatient.percentile && (
                <Badge 
                  variant="outline" 
                  className="text-xs ml-1 px-2 py-0"
                  style={{ 
                    borderColor: latestPatient.percentile.color,
                    color: latestPatient.percentile.color
                  }}
                >
                  {latestPatient.percentile.percentile}th
                </Badge>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Main WHO Growth Charts Component
const WHOGrowthCharts = ({ growthRecords, baby }) => {
  const [activeChart, setActiveChart] = useState('weightForAge');
  
  // Determine sex from baby data - handle various formats and field names
  const babySex = (() => {
    // Check both 'sex' and 'gender' fields
    const sexValue = baby?.sex || baby?.gender;
    if (!sexValue) return 'male'; // Default
    
    const sex = sexValue.toLowerCase().trim();
    // Check for female variations
    if (sex === 'female' || sex === 'f' || sex === 'girl') return 'female';
    // Otherwise male
    return 'male';
  })();
  
  // Get WHO data based on sex
  const whoData = useMemo(() => ({
    weightForAge: babySex === 'female' ? WHO_WEIGHT_FOR_AGE_GIRLS : WHO_WEIGHT_FOR_AGE_BOYS,
    heightForAge: babySex === 'female' ? WHO_HEIGHT_FOR_AGE_GIRLS : WHO_HEIGHT_FOR_AGE_BOYS,
    weightForHeight: babySex === 'female' ? WHO_WEIGHT_FOR_HEIGHT_GIRLS : WHO_WEIGHT_FOR_HEIGHT_BOYS,
    headCirc: babySex === 'female' ? WHO_HEAD_CIRC_FOR_AGE_GIRLS : WHO_HEAD_CIRC_FOR_AGE_BOYS,
    weightForAge5_18: babySex === 'female' ? WHO_WEIGHT_FOR_AGE_5_18_GIRLS : WHO_WEIGHT_FOR_AGE_5_18_BOYS,
    heightForAge5_18: babySex === 'female' ? WHO_HEIGHT_FOR_AGE_5_18_GIRLS : WHO_HEIGHT_FOR_AGE_5_18_BOYS,
    bmi: babySex === 'female' ? WHO_BMI_FOR_AGE_GIRLS : WHO_BMI_FOR_AGE_BOYS
  }), [babySex]);

  // Process patient data with percentile calculations
  const patientData = useMemo(() => {
    if (!growthRecords || growthRecords.length === 0 || !baby?.date_of_birth) {
      return { 
        weightForAge: [], heightForAge: [], weightForHeight: [], headCirc: [],
        weight0_18: [], height0_18: [], bmi: []
      };
    }

    const weightForAge = [];
    const heightForAge = [];
    const weightForHeight = [];
    const headCirc = [];
    const weight0_18 = [];
    const height0_18 = [];
    const bmiData = [];

    // Sort records by date (oldest first for charting)
    const sortedRecords = [...growthRecords].sort((a, b) => {
      const dateA = new Date(a.date_recorded || a.date);
      const dateB = new Date(b.date_recorded || b.date);
      return dateA - dateB;
    });

    sortedRecords.forEach(record => {
      const ageMonths = calculateAgeInMonths(baby.date_of_birth, record.date_recorded || record.date);
      
      if (ageMonths === null) return;

      // Parse numbers safely
      const weight = parseFloat(record.weight);
      const height = parseFloat(record.height);
      const headCircumference = parseFloat(record.head_circumference);

      // 0-5 years charts (0-60 months)
      if (ageMonths <= 60) {
        if (!isNaN(weight) && weight > 0) {
          const percentile = calculatePercentile(whoData.weightForAge, ageMonths, weight);
          weightForAge.push({ ageMonths, value: weight, percentile, record });
        }

        if (!isNaN(height) && height > 0) {
          const percentile = calculatePercentile(whoData.heightForAge, ageMonths, height);
          heightForAge.push({ ageMonths, value: height, percentile, record });
        }

        if (!isNaN(weight) && !isNaN(height) && weight > 0 && height > 0) {
          const percentile = calculatePercentile(whoData.weightForHeight, height, weight);
          weightForHeight.push({ height, value: weight, ageMonths, percentile, record });
        }

        if (!isNaN(headCircumference) && headCircumference > 0) {
          const percentile = calculatePercentile(whoData.headCirc, ageMonths, headCircumference);
          headCirc.push({ ageMonths, value: headCircumference, percentile, record });
        }
      }

      // 0-18 years charts (0-216 months)
      if (ageMonths <= 216) {
        if (!isNaN(weight) && weight > 0) {
          const dataSource = ageMonths <= 60 ? whoData.weightForAge : whoData.weightForAge5_18;
          const percentile = calculatePercentile(dataSource, ageMonths, weight);
          weight0_18.push({ ageMonths, value: weight, percentile, record });
        }

        if (!isNaN(height) && height > 0) {
          const dataSource = ageMonths <= 60 ? whoData.heightForAge : whoData.heightForAge5_18;
          const percentile = calculatePercentile(dataSource, ageMonths, height);
          height0_18.push({ ageMonths, value: height, percentile, record });
        }

        // BMI (typically tracked from age 2+)
        if (!isNaN(weight) && !isNaN(height) && weight > 0 && height > 0) {
          const bmi = calculateBMI(weight, height);
          if (bmi && ageMonths >= 24) {
            const percentile = calculatePercentile(whoData.bmi, ageMonths, bmi);
            bmiData.push({ ageMonths, value: bmi, percentile, record });
          }
        }
      }
    });

    return { 
      weightForAge, heightForAge, weightForHeight, headCirc,
      weight0_18, height0_18, bmi: bmiData
    };
  }, [growthRecords, baby, whoData]);

  // Calculate growth status for latest measurements with full details
  const growthAssessment = useMemo(() => {
    // First try to use the processed patient data
    let latestWeight = patientData.weightForAge[patientData.weightForAge.length - 1];
    let latestHeight = patientData.heightForAge[patientData.heightForAge.length - 1];
    let latestHeadCirc = patientData.headCirc[patientData.headCirc.length - 1];
    let ageMonths = latestWeight?.ageMonths || latestHeight?.ageMonths || latestHeadCirc?.ageMonths;
    let bmiValue = null;
    let bmiPercentile = null;
    
    // If no processed data, calculate directly from latest record
    if (!latestWeight && !latestHeight && growthRecords && growthRecords.length > 0 && baby?.date_of_birth) {
      // Get the most recent record
      const sortedRecords = [...growthRecords].sort((a, b) => {
        const dateA = new Date(a.date_recorded || a.date);
        const dateB = new Date(b.date_recorded || b.date);
        return dateB - dateA; // Most recent first
      });
      
      const latestRecord = sortedRecords[0];
      ageMonths = calculateAgeInMonths(baby.date_of_birth, latestRecord.date_recorded || latestRecord.date);
      
      // If still no valid age, default to 0 (newborn)
      if (ageMonths === null) ageMonths = 0;
      
      const weight = parseFloat(latestRecord.weight);
      const height = parseFloat(latestRecord.height);
      const headCircumference = parseFloat(latestRecord.head_circumference);
      
      // Only calculate WHO percentiles for children/teens (0-18 years = 0-216 months)
      // Adults (>216 months) should only use BMI assessment
      if (!isNaN(weight) && weight > 0 && ageMonths <= 216) {
        // Use appropriate data source based on age
        const weightDataSource = ageMonths <= 60 ? whoData.weightForAge : whoData.weightForAge5_18;
        const percentile = calculatePercentile(weightDataSource, ageMonths, weight);
        latestWeight = { ageMonths, value: weight, percentile };
      }
      
      if (!isNaN(height) && height > 0 && ageMonths <= 216) {
        // Use appropriate data source based on age
        const heightDataSource = ageMonths <= 60 ? whoData.heightForAge : whoData.heightForAge5_18;
        const percentile = calculatePercentile(heightDataSource, ageMonths, height);
        latestHeight = { ageMonths, value: height, percentile };
      }
      
      if (!isNaN(headCircumference) && headCircumference > 0 && ageMonths <= 60) {
        const percentile = calculatePercentile(whoData.headCirc, ageMonths, headCircumference);
        latestHeadCirc = { ageMonths, value: headCircumference, percentile };
      }
      
      // Calculate BMI for everyone, but BMI percentile only for teens (5-20 years)
      if (!isNaN(weight) && !isNaN(height) && weight > 0 && height > 0) {
        bmiValue = calculateBMI(weight, height);
        // Calculate BMI percentile for teens (60-240 months = 5-20 years)
        if (ageMonths > 60 && ageMonths <= 240 && bmiValue) {
          bmiPercentile = calculatePercentile(whoData.bmi, ageMonths, bmiValue);
        }
      }
    } else {
      // Get BMI from the latest record if we have processed data
      if (growthRecords && growthRecords.length > 0) {
        const sortedRecords = [...growthRecords].sort((a, b) => {
          const dateA = new Date(a.date_recorded || a.date);
          const dateB = new Date(b.date_recorded || b.date);
          return dateB - dateA;
        });
        const latestRecord = sortedRecords[0];
        const weight = parseFloat(latestRecord.weight);
        const height = parseFloat(latestRecord.height);
        if (!isNaN(weight) && !isNaN(height) && weight > 0 && height > 0) {
          bmiValue = calculateBMI(weight, height);
          
          // Get age for BMI percentile calculation
          const recordAge = calculateAgeInMonths(baby?.date_of_birth, latestRecord.date_recorded || latestRecord.date);
          // Calculate BMI percentile for teens (60-240 months = 5-20 years)
          if (recordAge && recordAge > 60 && recordAge <= 240 && bmiValue) {
            bmiPercentile = calculatePercentile(whoData.bmi, recordAge, bmiValue);
          }
          // Update ageMonths if not set
          if (!ageMonths && recordAge) {
            ageMonths = recordAge;
          }
        }
      }
    }
    
    // Determine if child is over WHO chart age (>5 years = >60 months)
    const isOverWHOAge = ageMonths !== null && ageMonths !== undefined && ageMonths > 60;
    
    return {
      statuses: getGrowthStatus(latestWeight?.percentile, latestHeight?.percentile),
      weightPercentile: latestWeight?.percentile,
      heightPercentile: latestHeight?.percentile,
      headCircPercentile: latestHeadCirc?.percentile,
      ageMonths: ageMonths,
      bmiValue: bmiValue,
      bmiPercentile: bmiPercentile,
      isOverWHOAge: isOverWHOAge
    };
  }, [patientData, growthRecords, baby, whoData]);

  // Generate chart data
  const chartData = useMemo(() => ({
    weightForAge: generateChartData(whoData.weightForAge, [0, 60]),
    heightForAge: generateChartData(whoData.heightForAge, [0, 60]),
    weightForHeight: generateWeightForHeightData(whoData.weightForHeight, [45, 120]),
    headCirc: generateChartData(whoData.headCirc, [0, 60]),
    weight0_18: generateChartData0to18(whoData.weightForAge, whoData.weightForAge5_18, [0, 216]),
    height0_18: generateChartData0to18(whoData.heightForAge, whoData.heightForAge5_18, [0, 216]),
    bmi: generateChartData(whoData.bmi, [60, 216])
  }), [whoData]);

  // Chart tabs in requested order - NO ICONS
  const charts = [
    { id: 'weightForAge', label: 'Weight (0-5y)' },
    { id: 'heightForAge', label: 'Height (0-5y)' },
    { id: 'weightForHeight', label: 'Weight/Height' },
    { id: 'headCirc', label: 'Head Circ.' },
    { id: 'weight0_18', label: 'Weight (0-18y)' },
    { id: 'height0_18', label: 'Height (0-18y)' },
    { id: 'bmi', label: 'BMI (0-18y)' }
  ];

  // Check if we have any data to display
  const hasData = growthRecords && growthRecords.length > 0;

  return (
    <div className="space-y-5">
      {/* Growth Status Alert */}
      {hasData && (
        <GrowthStatusAlert 
          statuses={growthAssessment.statuses}
          weightPercentile={growthAssessment.weightPercentile}
          heightPercentile={growthAssessment.heightPercentile}
          headCircPercentile={growthAssessment.headCircPercentile}
          ageMonths={growthAssessment.ageMonths}
          sex={babySex}
          bmiValue={growthAssessment.bmiValue}
          bmiPercentile={growthAssessment.bmiPercentile}
          isOverWHOAge={growthAssessment.isOverWHOAge}
        />
      )}
      
      {/* Chart Type Tabs - Clean, modern design without icons */}
      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
        {charts.map(chart => (
          <button
            key={chart.id}
            onClick={() => setActiveChart(chart.id)}
            className={`px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all duration-200 ${
              activeChart === chart.id 
                ? 'bg-blue-500 text-white shadow-md shadow-blue-200' 
                : 'bg-white text-gray-600 hover:bg-blue-50 hover:text-blue-600 border border-gray-200'
            }`}
          >
            {chart.label}
          </button>
        ))}
      </div>

      {/* Active Chart */}
      {activeChart === 'weightForAge' && (
        <GrowthChart
          title="Weight-for-Age (Birth to 5 Years)"
          data={chartData.weightForAge}
          patientData={patientData.weightForAge}
          xKey="age"
          yLabel="WEIGHT (kilograms)"
          xLabel="AGE (months)"
          yUnit="kg"
        />
      )}
      
      {activeChart === 'heightForAge' && (
        <GrowthChart
          title="Height-for-Age (Birth to 5 Years)"
          data={chartData.heightForAge}
          patientData={patientData.heightForAge}
          xKey="age"
          yLabel="HEIGHT (centimeters)"
          xLabel="AGE (months)"
          yUnit="cm"
        />
      )}
      
      {activeChart === 'weightForHeight' && (
        <GrowthChart
          title="Weight-for-Height (Birth to 5 Years)"
          data={chartData.weightForHeight}
          patientData={patientData.weightForHeight}
          xKey="height"
          yLabel="WEIGHT (kilograms)"
          xLabel="HEIGHT (centimeters)"
          yUnit="kg"
          chartType="weightForHeight"
        />
      )}
      
      {activeChart === 'headCirc' && (
        <GrowthChart
          title="Head Circumference-for-Age (Birth to 5 Years)"
          data={chartData.headCirc}
          patientData={patientData.headCirc}
          xKey="age"
          yLabel="HEAD CIRC. (centimeters)"
          xLabel="AGE (months)"
          yUnit="cm"
        />
      )}

      {activeChart === 'weight0_18' && (
        <GrowthChart
          title="Weight-for-Age (0 to 18 Years)"
          data={chartData.weight0_18}
          patientData={patientData.weight0_18}
          xKey="age"
          yLabel="WEIGHT (kilograms)"
          xLabel="AGE (years)"
          yUnit="kg"
          chartType="age18"
          formatXAxis="years"
        />
      )}

      {activeChart === 'height0_18' && (
        <GrowthChart
          title="Height-for-Age (0 to 18 Years)"
          data={chartData.height0_18}
          patientData={patientData.height0_18}
          xKey="age"
          yLabel="HEIGHT (centimeters)"
          xLabel="AGE (years)"
          yUnit="cm"
          chartType="age18"
          formatXAxis="years"
        />
      )}

      {activeChart === 'bmi' && (
        <GrowthChart
          title="BMI-for-Age (2 to 18 Years)"
          data={chartData.bmi}
          patientData={patientData.bmi}
          xKey="age"
          yLabel="BMI (kg/m²)"
          xLabel="AGE (years)"
          yUnit="kg/m²"
          chartType="age18"
          formatXAxis="years"
        />
      )}

      {/* Info Note */}
      <div className="text-center text-xs text-gray-400 pt-2">
        Based on WHO Child Growth Standards
      </div>
    </div>
  );
};

export default WHOGrowthCharts;
